import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { staffApi } from '../../lib/http';
import {
  PortalButton,
  PortalCard,
  PortalInput,
  PortalModal,
  PortalStatus,
} from '../../components/portal/PortalElements';

function parseQrPayload(value) {
  const trimmed = value.trim();
  if (!trimmed) {
    return { bookingId: '', qrToken: '' };
  }

  try {
    const parsed = JSON.parse(trimmed);
    return {
      bookingId: String(parsed.bookingId || '').trim(),
      qrToken: String(parsed.qrToken || '').trim(),
    };
  } catch (error) {
    return { bookingId: trimmed, qrToken: '' };
  }
}

function normalizeScannerError(error, fallback) {
  const rawMessage = error instanceof Error && error.message ? error.message : fallback;
  const message = rawMessage.toLowerCase();

  if (message.includes('notallowederror') || message.includes('permission denied')) {
    return 'Camera permission was denied. Allow camera access and try again.';
  }
  if (message.includes('notfounderror') || message.includes('requested device not found')) {
    return 'No camera was found on this device. You can upload the QR image instead.';
  }
  if (message.includes('notreadableerror') || message.includes('could not start video source')) {
    return 'The camera is busy in another app or tab. Close it there and try again.';
  }
  if (message.includes('overconstrainederror')) {
    return 'This device could not open the preferred camera. Try another camera or upload the QR image.';
  }
  if (message.includes('invalid or expired qr code')) {
    return 'This QR code is invalid or has expired. Ask the guest to open the latest booking QR.';
  }
  if (message.includes('network error') || message.includes('timeout')) {
    return 'The scanner could not reach the server. Check the connection and try again.';
  }

  return rawMessage;
}

export default function StaffScanner() {
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);
  const scanTimerRef = useRef(null);
  const autoStartRef = useRef(false);
  const isSubmittingRef = useRef(false);
  const [manualValue, setManualValue] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSecureContext, setIsSecureContext] = useState(true);
  const [cameraStatus, setCameraStatus] = useState('Opening scanner...');
  const [scannerMode, setScannerMode] = useState('Waiting for camera...');
  const [isCameraLive, setIsCameraLive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessingScan, setIsProcessingScan] = useState(false);
  const [success, setSuccess] = useState(false);
  const [scannerAlert, setScannerAlert] = useState(null);
  const [cameraFacingMode, setCameraFacingMode] = useState('environment');
  const validatedBooking = scanResult?.valid ? scanResult.booking : undefined;

  useEffect(() => {
    setIsSecureContext(window.isSecureContext);
    if (!window.isSecureContext) {
      setCameraStatus('Camera access requires HTTPS or localhost. Open this staff panel over a secure origin to prompt for permission.');
    }
  }, []);

  useEffect(() => {
    if (!isSecureContext || autoStartRef.current) {
      return;
    }

    autoStartRef.current = true;
    void startScan();

    return () => {
      stopScan();
    };
  }, [isSecureContext]);

  function stopScan() {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (scanTimerRef.current) {
      window.clearInterval(scanTimerRef.current);
    }
    scanTimerRef.current = null;
    setIsCameraLive(false);
    setScannerMode('Scanner stopped');
  }

  function showScannerAlert(error, fallback, title = 'Scanner issue') {
    const message = normalizeScannerError(error, fallback);
    stopScan();
    setIsProcessingScan(false);
    setCameraStatus(message);
    setScannerAlert({ title, message });
  }

  async function validateBooking(bookingIdValue, qrTokenValue) {
    try {
      const response = await staffApi.post('/staff/scan-qr', {
        bookingId: bookingIdValue,
        qrToken: qrTokenValue,
      });
      setScanResult(response.data);
      if (!response.data.valid) {
        setCameraStatus('Booking is not eligible for check-in.');
      } else {
        setCameraStatus('Booking validated. Ready to check in.');
      }
      toast.success(response.data.valid ? 'Booking validated.' : 'Booking is not eligible for check-in.');
    } catch (error) {
      setScanResult(null);
      showScannerAlert(error, 'Unable to validate QR.', 'Scan failed');
    } finally {
      setLoading(false);
      setIsProcessingScan(false);
      isSubmittingRef.current = false;
    }
  }

  async function submitParsedScan(parsed) {
    if (!parsed.bookingId || !parsed.qrToken || isSubmittingRef.current) {
      showScannerAlert(
        new Error('QR code detected, but the secure booking token was missing.'),
        'QR code detected, but the secure booking token was missing.',
        'Invalid QR code',
      );
      return;
    }

    isSubmittingRef.current = true;
    setLoading(true);
    setIsProcessingScan(true);
    stopScan();
    await validateBooking(parsed.bookingId, parsed.qrToken);
  }

  async function validateManual() {
    const parsed = parseQrPayload(manualValue);
    if (!parsed.bookingId || !parsed.qrToken) {
      toast.error('Enter the QR payload in this field so the booking ID and secure token can be verified.');
      return;
    }
    await submitParsedScan(parsed);
  }

  async function detectCodes(source) {
    if (!window.BarcodeDetector) {
      throw new Error('Automatic QR detection is not supported in this browser.');
    }
    const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
    return detector.detect(source);
  }

  function startDetectorLoop() {
    scanTimerRef.current = window.setInterval(async () => {
      if (!videoRef.current || isSubmittingRef.current) {
        return;
      }
      try {
        const codes = await detectCodes(videoRef.current);
        const value = codes[0]?.rawValue;
        if (value) {
          await submitParsedScan(parseQrPayload(value));
        }
      } catch (error) {
        showScannerAlert(error, 'QR detection failed while reading the camera feed.', 'Camera scan failed');
      }
    }, 1200);
  }

  async function startScan() {
    await startScanWithFacingMode(cameraFacingMode);
  }

  async function startScanWithFacingMode(facingMode) {
    try {
      setScannerAlert(null);
      stopScan();
      setIsProcessingScan(false);
      setScanResult(null);
      setSuccess(false);
      setCameraFacingMode(facingMode);
      setScannerMode('Starting camera...');

      if (!window.isSecureContext) {
        showScannerAlert(
          new Error('Camera access is blocked in insecure contexts. Use HTTPS or localhost.'),
          'Camera access is blocked in insecure contexts. Use HTTPS or localhost.',
          'Secure connection required',
        );
        return;
      }

      const mediaDevices = navigator.mediaDevices;
      if (!mediaDevices?.getUserMedia) {
        showScannerAlert(
          new Error('Camera access is not supported in this browser. Please upload a QR image instead.'),
          'Camera access is not supported in this browser. Please upload a QR image instead.',
          'Camera unavailable',
        );
        return;
      }

      setCameraStatus('Requesting camera permission...');
      const stream = await mediaDevices.getUserMedia({ video: { facingMode } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setIsCameraLive(true);
      setCameraStatus('Point the camera at the QR code.');
      if ('BarcodeDetector' in window) {
        setScannerMode('Live camera with BarcodeDetector');
        startDetectorLoop();
      } else {
        setScannerMode('Manual and upload fallback only');
        setCameraStatus('Camera is active, but this browser does not support automatic QR reading. Use upload or paste fallback.');
      }
    } catch (error) {
      setScannerMode('Camera unavailable');
      setIsProcessingScan(false);
      showScannerAlert(error, 'Camera unavailable.', 'Camera unavailable');
    }
  }

  async function toggleCameraFacingMode() {
    const nextFacingMode = cameraFacingMode === 'environment' ? 'user' : 'environment';
    await startScanWithFacingMode(nextFacingMode);
  }

  async function decodeQrFromFile(file) {
    const bitmap = await createImageBitmap(file);
    try {
      const codes = await detectCodes(bitmap);
      const value = codes[0]?.rawValue;
      if (!value) {
        throw new Error('No QR code was detected in the uploaded image.');
      }
      return value;
    } finally {
      bitmap.close?.();
    }
  }

  async function handleQrUpload(event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setScannerAlert(null);
    setIsUploading(true);
    setCameraStatus(`Reading uploaded QR image: ${file.name}`);
    try {
      const decodedValue = await decodeQrFromFile(file);
      await submitParsedScan(parseQrPayload(decodedValue));
    } catch (error) {
      showScannerAlert(error, 'Unable to process uploaded QR.', 'Upload failed');
    } finally {
      event.target.value = '';
      setIsUploading(false);
    }
  }

  async function completeCheckIn() {
    if (!scanResult?.booking?.bookingId || !scanResult?.booking?.qrToken) {
      toast.error('Validate a booking first.');
      return;
    }

    setLoading(true);
    try {
      const response = await staffApi.post('/staff/check-in', {
        bookingId: scanResult.booking.bookingId,
        qrToken: scanResult.booking.qrToken,
      });
      toast.success(`Checked in ${response.data.customerName}.`);
      setSuccess(true);
      setCameraStatus(`Checked in ${response.data.customerName}.`);
      window.setTimeout(() => {
        setScanResult(null);
        setSuccess(false);
        setManualValue('');
        void startScan();
      }, 2500);
    } catch (error) {
      showScannerAlert(error, 'Unable to complete check-in.', 'Check-in failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="portal-scanner-shell">
      <PortalCard>
        <div className="portal-section-head">
          <div>
            <p className="portal-kicker">Validation</p>
            <h2>Staff scanner</h2>
          </div>
        </div>
        <div className="portal-scanner-badges">
          <div className={`portal-scanner-badge ${isCameraLive ? 'portal-scanner-badge-live' : ''}`}>
            {isCameraLive ? 'Camera live' : 'Camera not live'}
          </div>
          <div className="portal-scanner-badge">{scannerMode}</div>
        </div>
        <div className="portal-scanner-status">{cameraStatus}</div>

        {!scanResult ? (
          <>
            <div className="portal-scanner-stage">
              <video ref={videoRef} className="portal-scanner-video" playsInline muted autoPlay />
              <div className="portal-scanner-switch">
                <PortalButton variant="secondary" onClick={() => void toggleCameraFacingMode()}>
                  Switch
                </PortalButton>
              </div>
              {isCameraLive && !isUploading && !scannerAlert && !isProcessingScan ? (
                <div className="portal-scanner-overlay">
                  <div className="portal-scanner-frame" />
                </div>
              ) : null}
            </div>
            <div className="portal-action-row">
              <PortalButton onClick={() => void startScan()} disabled={!isSecureContext}>
                Retry Camera
              </PortalButton>
              <PortalButton variant="secondary" onClick={stopScan}>
                Stop
              </PortalButton>
              <PortalButton variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
                {isUploading ? 'Uploading...' : 'Upload QR'}
              </PortalButton>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              onChange={handleQrUpload}
            />
          </>
        ) : null}
      </PortalCard>

      {!scanResult ? (
        <PortalCard>
          <div className="portal-section-head">
            <div>
              {/* <p className="portal-kicker">Fallback</p> */}
              <h2>Manual CheckIn</h2>
            </div>
          </div>
          <PortalInput
            label="Booking ID"
            value={manualValue}
            onChange={(event) => setManualValue(event.target.value)}
            placeholder='Booking ID'
          />
          <div className="portal-action-row">
            <PortalButton onClick={() => void validateManual()} disabled={loading}>
              {loading ? 'Checking...' : 'Validate booking'}
            </PortalButton>
          </div>
        </PortalCard>
      ) : null}

      {scanResult ? (
        <PortalCard>
          <div className="portal-action-row">
            <PortalStatus tone={scanResult.valid ? 'success' : 'danger'}>
              {scanResult.valid ? 'Valid booking' : scanResult.reason || 'Invalid booking'}
            </PortalStatus>
          </div>
          {validatedBooking ? (
            <>
              <div className="portal-detail-grid">
                <div><strong>Booking</strong><span>{validatedBooking.bookingId}</span></div>
                <div><strong>Guest</strong><span>{validatedBooking.customerName}</span></div>
                <div><strong>Phone</strong><span>{validatedBooking.phone}</span></div>
                <div><strong>Email</strong><span>{validatedBooking.email}</span></div>
                <div><strong>Schedule</strong><span>{validatedBooking.date} at {validatedBooking.time}</span></div>
                <div><strong>Table preference</strong><span>{validatedBooking.tablePreference || '-'}</span></div>
                <div><strong>Status</strong><span>{validatedBooking.status}</span></div>
              </div>
              <div className="portal-action-row">
                <PortalButton onClick={() => void completeCheckIn()} disabled={loading}>
                  {loading ? 'Checking in...' : 'Complete check-in'}
                </PortalButton>
                <PortalButton variant="secondary" onClick={() => { setScanResult(null); void startScan(); }}>
                  Scan again
                </PortalButton>
              </div>
            </>
          ) : success ? (
            <div className="portal-scanner-status">Checked in successfully. Returning to the scanner automatically.</div>
          ) : (
            <div className="portal-scanner-status">This QR cannot be checked in right now. Please scan again.</div>
          )}
        </PortalCard>
      ) : null}

      <PortalModal open={Boolean(scannerAlert)} title={scannerAlert?.title || 'Scanner issue'} onClose={() => setScannerAlert(null)}>
        <p>{scannerAlert?.message}</p>
      </PortalModal>
    </div>
  );
}
